import React from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";

import { Label } from "../Label";
import { Field } from "../Field";
import { Error } from "../Error";
import { sanitiseString } from "../../Utils";

import type { EnabledActions, ToolbarAction } from "./types";
import { ALL_TOOLBAR_ACTIONS } from "./types";
import { handleEditorError, editorNodes } from "./editorConfig";
import { Toolbar } from "./Toolbar";
import {
  HtmlOnChangePlugin,
  HtmlHydrationPlugin,
  SingleLinePlugin,
  PasteInterceptorPlugin,
} from "./Plugins";
import type { PasteResult } from "./Plugins/PasteInterceptorPlugin";
import { EditorContainer, EditorContentEditable } from "./Styles";

const DEFAULT_ROWS: number = 8;

export interface Props {
  id: string;
  dataTestId: string;
  label?: React.ComponentProps<typeof Label>;
  ariaLabel?: string;
  fieldClasses?: string;
  error?: React.ComponentProps<typeof Error>;
  multiline?: boolean;
  rows?: number;
  width?: string;
  editable?: boolean;
  enabledActions?: EnabledActions;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onPaste?: (text: string) => PasteResult;
}

export const RichTextEditor = (props: Props) => {
  const baseId = sanitiseString(props.id);
  const baseTestId = sanitiseString(props.dataTestId);

  const multiline: boolean = props.multiline ?? true;
  const editable: boolean = props.editable ?? true;
  const rows: number = props.rows ?? DEFAULT_ROWS;
  const enabledActions: ToolbarAction[] = [
    ...(props.enabledActions ?? ALL_TOOLBAR_ACTIONS),
  ];
  const emphasis: "bold" | "highlight" | undefined = enabledActions.includes("highlight")
    ? "highlight"
    : enabledActions.includes("bold")
      ? "bold"
      : undefined;
  const ariaLabel: string | undefined = props.ariaLabel || props.label?.text;

  const initialConfig = {
    namespace: "RichTextEditor",
    onError: handleEditorError,
    editable,
    nodes: editorNodes,
  };

  // Blocks Enter in single-line mode
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!multiline && event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      {/* Lets parents intercept pastes inside Lexical's editor */}
      <PasteInterceptorPlugin onPaste={props.onPaste} />
      {/* Enables undo/redo */}
      <HistoryPlugin />
      {/* Enables bulleted list support */}
      <ListPlugin />
      {/* Enables hyperlink support */}
      <LinkPlugin />
      {/* Serialises editor state to HTML for onChange and onBlur */}
      <HtmlOnChangePlugin onChange={props.onChange} onBlur={props.onBlur} />
      {/* Loads initial HTML content into the editor on mount */}
      {props.value !== undefined && <HtmlHydrationPlugin html={props.value} />}
      {/* Blocks Enter/paragraph/linebreak when in single-line mode */}
      {!multiline && <SingleLinePlugin />}

      <Field
        id={`${baseId}-field`}
        classes={props.fieldClasses}
        dataTestId={`${baseTestId}-field-test`}
        error={props.error}
      >
        <>
          {props.label?.text && (
            <Label
              for={`${baseId}-input`}
              id={props.label.id}
              text={props.label.text}
              classes={props.label.classes}
              description={props.label.description}
              attributes={props.label.attributes}
              dataTestId={`${baseTestId}-label-test`}
            />
          )}
          <EditorContainer
            $width={props.width}
            data-testid={`${baseTestId}-editor-test`}
          >
            <Toolbar
              baseId={baseId}
              baseTestId={baseTestId}
              enabledActions={enabledActions}
              disabled={!editable}
              ariaControls={`${baseId}-input`}
            />
            <RichTextPlugin
              contentEditable={
                <EditorContentEditable
                  id={`${baseId}-input`}
                  data-testid={`${baseTestId}-input-test`}
                  $singleLine={!multiline}
                  $rows={multiline ? rows : 1}
                  $emphasis={emphasis}
                  onKeyDown={handleKeyDown}
                  aria-label={ariaLabel}
                />
              }
              placeholder={null}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </EditorContainer>
        </>
      </Field>
    </LexicalComposer>
  );
};
